/* ─────────────────────────────────────────────────────────────
   Combobox — Searchable select dropdown built on Radix Popover.
   Supports single and multi selection, keyboard navigation,
   filtering, grouped options, and custom rendering.

   Styled for the Digital Architect design system:
   - surface-high trigger (soft inset, no full border)
   - surface-elevated dropdown panel (ambient shadow)
   - primary-subtle highlight on focus
   ───────────────────────────────────────────────────────────── */

import * as React from 'react'
import { useId, useState, useRef, useCallback, useMemo, useEffect } from 'react'
import { ChevronDown, Check, Search, X } from 'lucide-react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { cn } from '@/lib/cn'
import { Badge } from '@/components/ui/Badge'
import { Text } from '@/components/ui/Text'

export interface ComboboxOption {
  value: string
  label: string
  description?: string
  group?: string
  disabled?: boolean
  /** When true, bypasses client-side substring filter (use for server-matched suggestions). */
  skipFilter?: boolean
  /** When true, excluded from the visible list but still resolvable for chip labels. */
  hidden?: boolean
  /**
   * Optional non-color provenance/status marker rendered next to the label via `Badge`.
   * Use for signals that must never be conveyed by color alone (e.g. "Other organisation").
   * Distinct from `description` — that stays a stable per-option field, this is a marker.
   */
  badge?: string
}

// ── Shared base props ────────────────────────────────────────────────────────

interface ComboboxBaseProps {
  options: ComboboxOption[]
  /** Called whenever the internal search query changes (enables ES fallback in PlacesSelect). */
  onSearch?: (query: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  disabled?: boolean
  /** Applied to the trigger button. Pass `w-auto` in filter contexts to override the default full-width. */
  className?: string
  /** HTML id for the trigger button — enables getElementById lookups and label[htmlFor] wiring. */
  id?: string
  /**
   * Id of an external element (e.g. a heading/question) that names this control.
   * When set, wins over the derived `aria-label={placeholder}` for the accessible name —
   * standard aria-labelledby-over-aria-label precedence.
   */
  ariaLabelledBy?: string
  /** Accessible name when the visible placeholder is intentionally short. */
  ariaLabel?: string
  /**
   * Id of an element describing this control (e.g. a validation reason rendered beneath it).
   * Mirrors `ariaLabelledBy` above; applied to whichever element currently owns the combobox
   * role, so the description is announced in both the open and closed states.
   */
  ariaDescribedBy?: string
  grouped?: boolean
  clearable?: boolean
  /** Rendered at the bottom of the dropdown panel (domain-specific extras, e.g. a "Direct" toggle). */
  footer?: React.ReactNode
  /**
   * `field` (default) draws the bordered select-like trigger. `link` draws a primary text link
   * that always shows `placeholder` — for an ACTION picker that never holds a selection, like
   * create-invoice's "＋ Add from service catalogue". The dropdown, search and keyboard model are
   * identical; only the trigger's look changes, so no page hand-rolls a second picker.
   */
  trigger?: 'field' | 'link'
  /**
   * Max characters accepted by the dropdown's search input. Optional and additive — for a caller
   * whose `onSearch` feeds a server that rejects long strings (e.g. Health's 200-char `prefix`).
   */
  searchMaxLength?: number
  /**
   * Incremental ("load more") options — optional and additive. When `hasMore` is true, scrolling
   * the option list to (near) its end calls `onLoadMore` once; the caller appends the next page to
   * `options`. `isLoadingMore` shows a "Loading more…" row and suppresses repeat calls until the
   * page lands. Every existing caller passes none of these, so its behaviour is unchanged.
   */
  onLoadMore?: () => void
  hasMore?: boolean
  isLoadingMore?: boolean
}

// ── Discriminated union: single vs multi ─────────────────────────────────────

type SingleComboboxProps = ComboboxBaseProps & {
  mode?: 'single'
  value?: string
  onValueChange?: (value: string) => void
}

type MultiComboboxProps = ComboboxBaseProps & {
  /**
   * multi        — checkmark toggles, dropdown stays open, trigger shows label + count badge.
   * multi-chips  — same as multi; the caller renders removable chips below the trigger.
   * multi-inline — selected labels stay inside the field; options are toggled in the dropdown.
   */
  mode: 'multi' | 'multi-chips' | 'multi-inline'
  value?: string[]
  onValueChange?: (value: string[]) => void
}

export type ComboboxProps = SingleComboboxProps | MultiComboboxProps

// ── Component ────────────────────────────────────────────────────────────────

export function Combobox(props: ComboboxProps) {
  const {
    options,
    onSearch,
    placeholder = 'Select...',
    searchPlaceholder = 'Search...',
    emptyMessage = 'No results found',
    disabled = false,
    className,
    id,
    ariaLabelledBy,
    ariaLabel,
    ariaDescribedBy,
    grouped = false,
    clearable = false,
    footer,
    trigger = 'field',
    searchMaxLength,
    onLoadMore,
    hasMore = false,
    isLoadingMore = false,
  } = props
  const isLink = trigger === 'link'

  const mode = props.mode ?? 'single'
  const isSingle = mode === 'single'
  const isMulti = !isSingle
  const isInlineMulti = mode === 'multi-inline'

  // Normalise selected state to a consistent shape internally
  const singleValue: string = isSingle ? ((props as SingleComboboxProps).value ?? '') : ''
  const multiValues: string[] = isMulti ? ((props as MultiComboboxProps).value ?? []) : []

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // ARIA APG combobox pattern: the listbox and its options need stable ids so the
  // trigger/input can reference the currently-highlighted option via aria-activedescendant.
  const listboxId = useId()
  const optionId = useCallback((index: number) => `${listboxId}-option-${index}`, [listboxId])

  // A Popover portaled to <body> (the default, needed to escape ancestor overflow/
  // z-index elsewhere) lands OUTSIDE a Radix Dialog's own DOM subtree. The Dialog's
  // scroll lock doesn't recognize that externally-portaled region as a legitimate
  // nested scroll area, so wheel-scrolling the option list silently does nothing.
  // Rendering into the Dialog's own content div instead (still floating-positioned
  // correctly via Radix's floating-ui logic) keeps it a real descendant, which the
  // scroll lock leaves alone — so this only applies when a Dialog ancestor exists;
  // every other usage keeps portaling to <body> exactly as before.
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null)
  // Kept, with the rule named rather than the family: this reads the DOM for a dialog ancestor
  // so the dropdown portals INSIDE an open dialog instead of behind it. An ancestor cannot be
  // known during render — the element does not exist yet — so the effect is the only place this
  // can run, and getting DOM-derived state into render means setState. That is the "subscribe to
  // an external system" case the rule's own message carves out; the DOM is the external system.
  //
  // This diagnostic only became visible when the `react-hooks/refs` disable above was removed:
  // any react-hooks suppression makes the compiler skip the whole component, so one suppression
  // was hiding an unrelated one.
  useEffect(() => {
    if (!open) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reacts to an external system
    setPortalContainer(triggerRef.current?.closest<HTMLElement>('[role="dialog"]') ?? null)
  }, [open])

  const selectedOption = isSingle ? options.find((o) => o.value === singleValue) : null
  const hasSelection = isSingle ? !!selectedOption : multiValues.length > 0
  const inlineMultiLabel = isInlineMulti
    ? multiValues.map((value) => options.find((option) => option.value === value)?.label ?? value).join(', ')
    : ''

  const filtered = useMemo(() => {
    const visible = options.filter((o) => !o.hidden)
    if (!query.trim()) return visible
    const q = query.toLowerCase()
    return visible.filter(
      (o) =>
        !o.disabled &&
        (o.skipFilter ||
          o.label.toLowerCase().includes(q) ||
          o.description?.toLowerCase().includes(q) ||
          o.value.toLowerCase().includes(q)),
    )
  }, [options, query])

  const groups = useMemo(() => {
    if (!grouped) return null
    const map = new Map<string, ComboboxOption[]>()
    for (const o of filtered) {
      const g = o.group ?? ''
      const list = map.get(g) ?? []
      list.push(o)
      map.set(g, list)
    }
    return map
  }, [filtered, grouped])

  const flatList = grouped && groups ? [...groups.values()].flat() : filtered

  // Activate an option: single mode closes + sets value; multi mode toggles + stays open.
  const handleActivate = useCallback(
    (optionValue: string) => {
      if (isSingle) {
        ;(props as SingleComboboxProps).onValueChange?.(optionValue)
        setOpen(false)
        setQuery('')
        triggerRef.current?.focus()
      } else {
        const current = (props as MultiComboboxProps).value ?? []
        const next = current.includes(optionValue)
          ? current.filter((v) => v !== optionValue)
          : [...current, optionValue]
        ;(props as MultiComboboxProps).onValueChange?.(next)
        // Keep dropdown open for multi-select
      }
    },
    [isSingle, props, setOpen, setQuery],
  )

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (isSingle) {
        ;(props as SingleComboboxProps).onValueChange?.('')
        onSearch?.('')
      } else {
        ;(props as MultiComboboxProps).onValueChange?.([])
      }
      setQuery('')
    },
    [isSingle, props, onSearch, setQuery],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightIndex((i) => (i + 1 >= flatList.length ? 0 : i + 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightIndex((i) => (i - 1 < 0 ? flatList.length - 1 : i - 1))
      } else if (e.key === 'Home') {
        e.preventDefault()
        setHighlightIndex(0)
      } else if (e.key === 'End') {
        e.preventDefault()
        setHighlightIndex(flatList.length - 1)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const item = flatList[highlightIndex]
        if (item && !item.disabled) handleActivate(item.value)
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setOpen(false)
        triggerRef.current?.focus()
      }
    },
    [flatList, highlightIndex, handleActivate, setHighlightIndex, setOpen],
  )

  // "Adjusting state when a prop changes" — React's documented pattern, which avoids the extra
  // render pass an effect would cost. The previous value is held in STATE, not a ref: the ref
  // version writes during render, and a render can be discarded (StrictMode double-renders,
  // concurrent React may abandon one) while the ref write is not undone — leaving the ref
  // claiming it already handled a change that never committed, so the reset silently stops
  // firing. State updates are queued and discarded with the render they belong to.
  const [prevQuery, setPrevQuery] = React.useState(query)
  if (prevQuery !== query) {
    setPrevQuery(query)
    if (highlightIndex !== 0) setHighlightIndex(0)
  }

  const [prevOpen, setPrevOpen] = React.useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    if (!open && query !== '') setQuery('')
    if (open && highlightIndex !== -1) setHighlightIndex(-1)
  }

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 10)
    } else {
      onSearch?.('')
    }
  }, [open, onSearch])

  useEffect(() => {
    if (!listRef.current) return
    const el = listRef.current.querySelector(`[data-index="${highlightIndex}"]`)
    if (el) el.scrollIntoView({ block: 'nearest' })
  }, [highlightIndex])

  // Load-more: fire once the list is scrolled within one row of its end. `isLoadingMore` gates
  // repeats, so a burst of scroll events while a page is in flight requests it exactly once.
  const handleListScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!onLoadMore || !hasMore || isLoadingMore) return
      const el = e.currentTarget
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) onLoadMore()
    },
    [onLoadMore, hasMore, isLoadingMore],
  )

  // Shared dropdown portal (identical for all modes)
  const dropdownPortal = (
    <PopoverPrimitive.Portal container={portalContainer ?? undefined}>
      <PopoverPrimitive.Content
        align="start"
        sideOffset={4}
        // Radix defaults Popover.Content to role="dialog" (it shares FocusScope/
        // DismissableLayer machinery with Dialog). That's wrong for a combobox
        // popup — the popup itself carries no ARIA role; the listbox inside it
        // (below) is the only semantic structure. Explicit `undefined` overrides
        // Radix's default (contentProps spreads after it — see @radix-ui/react-popover).
        role={undefined}
        className={cn(
          'z-50 w-[var(--radix-popover-trigger-width)] min-w-[220px] overflow-hidden',
          'rounded-lg bg-surface-elevated shadow-ambient',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-[0.98] data-[state=open]:zoom-in-[0.98]',
          'motion-reduce:animate-none motion-reduce:transition-none',
        )}
        onKeyDown={handleKeyDown}
      >
        {/* Search input — the ARIA APG "editable combobox" owner while open.
            aria-activedescendant is only meaningful on the element that both
            has focus and owns role="combobox" — this is that element; the
            trigger button below relinquishes role="combobox" while open so
            there is never more than one combobox object in the tree. */}
        <div className="flex items-center gap-2 px-3 py-2.5 bg-surface-muted/30">
          <Search size={13} className="text-text-subtle shrink-0" />
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-activedescendant={highlightIndex >= 0 ? optionId(highlightIndex) : undefined}
            aria-label={ariaLabel ?? placeholder}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              onSearch?.(e.target.value)
            }}
            placeholder={searchPlaceholder}
            maxLength={searchMaxLength}
            className="flex-1 bg-transparent text-sm text-text outline-none placeholder:text-text-subtle"
          />
          {query && (
            <button
              onClick={() => {
                setQuery('')
                onSearch?.('')
              }}
              className="p-0.5 hover:bg-surface-muted rounded-sm transition-colors"
            >
              <X size={11} className="text-text-subtle" />
            </button>
          )}
        </div>

        {/* Option list */}
        <div
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-busy={isLoadingMore || undefined}
          onScroll={onLoadMore ? handleListScroll : undefined}
          className="max-h-60 overflow-y-auto p-1.5"
        >
          {flatList.length === 0 && !isLoadingMore && (
            <div className="py-4 text-center text-body-sm text-text-subtle">
              {emptyMessage}
            </div>
          )}

          {grouped && groups ? (
            [...groups.entries()].map(([groupName, groupOptions]) => {
              const startIndex = flatList.indexOf(groupOptions[0])
              return (
                <div key={groupName}>
                  {groupName && (
                    <Text variant="label-sm" color="subtle" as="div" className="px-2.5 py-1.5">
                      {groupName}
                    </Text>
                  )}
                  {groupOptions.map((option, gi) => (
                    <OptionItem
                      key={option.value}
                      id={optionId(startIndex + gi)}
                      option={option}
                      selected={
                        isMulti
                          ? multiValues.includes(option.value)
                          : option.value === singleValue
                      }
                      highlighted={startIndex + gi === highlightIndex}
                      index={startIndex + gi}
                      onSelect={handleActivate}
                    />
                  ))}
                </div>
              )
            })
          ) : (
            flatList.map((option, i) => (
              <OptionItem
                key={option.value}
                id={optionId(i)}
                option={option}
                selected={
                  isMulti
                    ? multiValues.includes(option.value)
                    : option.value === singleValue
                }
                highlighted={i === highlightIndex}
                index={i}
                onSelect={handleActivate}
              />
            ))
          )}
          {isLoadingMore && (
            <Text as="div" variant="body-sm" color="subtle" className="py-2 text-center">
              Loading more…
            </Text>
          )}
        </div>

        {/* Footer slot — for domain-specific extras rendered after the option list */}
        {footer && <div className="border-t border-border">{footer}</div>}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild disabled={disabled}>
        <button
          ref={triggerRef}
          type="button"
          id={id}
          // The combobox role lives on the search input while it's open and
          // focused (below) — an ARIA combobox object must be the focused
          // element, and only one may exist at a time. Closed, this trigger
          // IS that object; open, it steps back to a plain disclosure button
          // (aria-haspopup still tells AT what activating it reveals).
          role={open ? undefined : 'combobox'}
          aria-haspopup="listbox"
          aria-label={ariaLabel ?? placeholder}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          aria-expanded={open}
          aria-controls={listboxId}
          className={cn(
            isLink
              ? cn(
                  'inline-flex items-center rounded-md px-2.5 py-1.5',
                  'text-body-sm font-semibold text-primary hover:bg-primary-subtle transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                )
              : cn(
                  'flex h-9 w-full items-center justify-between',
                  'rounded bg-surface-high px-3 py-2 text-sm',
                  'border transition-colors',
                  // Multi active state: persistent primary edge when selections exist
                  isMulti && multiValues.length > 0
                    ? 'border-primary text-primary'
                    : 'border-input-border text-text',
                  isSingle && !selectedOption && 'text-text-subtle',
                  'focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary',
                ),
            'disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
        >
          <span className="truncate">
            {isInlineMulti
              ? inlineMultiLabel || placeholder
              : isMulti || isLink
                ? placeholder
                : selectedOption
                  ? selectedOption.label
                  : placeholder}
          </span>
          {!isLink && <div className="flex items-center gap-1 shrink-0 ml-2">
            {isMulti && !isInlineMulti && multiValues.length > 0 && (
              <span className="rounded-full bg-primary/20 text-primary text-[10px] px-1.5 font-semibold leading-4">
                {multiValues.length}
              </span>
            )}
            {clearable && hasSelection && (
              <span
                role="button"
                tabIndex={-1}
                onClick={handleClear}
                className="p-0.5 hover:bg-surface-muted rounded-sm transition-colors"
              >
                <X size={12} className="text-text-subtle" />
              </span>
            )}
            <ChevronDown
              size={14}
              className={cn(
                'text-text-subtle opacity-60 transition-transform duration-200',
                open && 'rotate-180',
              )}
            />
          </div>}
        </button>
      </PopoverPrimitive.Trigger>
      {dropdownPortal}
    </PopoverPrimitive.Root>
  )
}

// ── OptionItem ───────────────────────────────────────────────────────────────

function OptionItem({
  id,
  option,
  selected,
  highlighted,
  index,
  onSelect,
}: {
  id: string
  option: ComboboxOption
  selected: boolean
  highlighted: boolean
  index: number
  onSelect: (value: string) => void
}) {
  return (
    <div
      id={id}
      data-index={index}
      role="option"
      aria-selected={selected}
      onClick={() => !option.disabled && onSelect(option.value)}
      className={cn(
        'relative flex items-center gap-2 rounded-sm px-2.5 py-2 text-sm cursor-pointer select-none',
        'transition-colors',
        highlighted && 'bg-primary-subtle',
        selected && 'text-primary font-medium',
        option.disabled && 'opacity-40 pointer-events-none',
        !highlighted && !selected && 'text-text-muted',
      )}
    >
      <span className="w-4 shrink-0 flex items-center justify-center">
        {selected && <Check size={13} className="text-primary" />}
      </span>
      <div className="flex-1 min-w-0">
        <span className="flex items-center gap-1.5 min-w-0">
          <span className="truncate">{option.label}</span>
          {option.badge && (
            <Badge variant="neutral" className="shrink-0 px-1.5 py-0 text-[10px] leading-4">
              {option.badge}
            </Badge>
          )}
        </span>
        {option.description && (
          <Text variant="body-sm" color="subtle" className="truncate block">{option.description}</Text>
        )}
      </div>
    </div>
  )
}
